import React, { useEffect, useState } from 'react'
import useCallApi from '../../../../hooks/useCallApi'
import Loading from '../../../../templates/UI/Loading/Loading'
import callAPI from '../../../../helpers/callAPI'
import Dollars from '../../../../templates/Dollars/Dollars'

export default function CurrencyRate({ showSystemMessage }) {
    const { data, dataLoaded, refetch } = useCallApi(`forex/current?currency=EUR`, 'GET', {}, [], showSystemMessage)

    const [currentRate, setCurrentRate] = useState(null)
    const [draftRate, setDraftRate] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const [ecbCurrentRate, setEcbCurrentRate] = useState(null)
    const [ecbAverageRate, setEcbAverageRate] = useState(null)
    const [ecbRatesLoaded, setEcbRatesLoaded] = useState(false)

    const extractRate = (rateData) => {
        if (!rateData) return null
        if (rateData.conv_rate_USD !== undefined) return rateData.conv_rate_USD
        if (rateData.data && rateData.data.conv_rate_USD !== undefined) return rateData.data.conv_rate_USD
        if (Array.isArray(rateData) && rateData.length > 0 && rateData[0].conv_rate_USD !== undefined) return rateData[0].conv_rate_USD
        if (rateData.rate !== undefined) return rateData.rate
        return null
    }

    useEffect(() => {
        if (!dataLoaded) return
        const rate = extractRate(data)
        if (rate !== null) {
            setCurrentRate(rate)
            setDraftRate(rate)
        }
    }, [dataLoaded, data])

    useEffect(() => {
        const fetchEcbRates = async () => {
            try {
                const result = await callAPI('forex/ecb-rates', 'GET')
                
                if (result.success) {
                    if (result.currentRate !== null && result.currentRate !== undefined) {
                        setEcbCurrentRate(result.currentRate)
                    }
                    if (result.averageRate !== null && result.averageRate !== undefined) {
                        setEcbAverageRate(result.averageRate)
                    }
                } else {
                    console.error('Failed to fetch ECB rates:', result.errors?.message)
                }
                
                setEcbRatesLoaded(true)
            } catch (error) {
                console.error('Error fetching ECB rates:', error)
                setEcbRatesLoaded(true)
            }
        }

        fetchEcbRates()
    }, [])

    const handleSave = async () => {
        const parsedRate = parseFloat(draftRate)

        if (Number.isNaN(parsedRate) || parsedRate <= 0) {
            if (showSystemMessage) {
                showSystemMessage('error', 'Enter a valid positive number.')
            }
            return
        }

        // Validate reasonable range for currency exchange rate (0.1 to 10.0)
        if (parsedRate < 0.1 || parsedRate > 10.0) {
            if (showSystemMessage) {
                showSystemMessage('error', 'Rate must be between 0.1 and 10.0. Please check your input.')
            }
            return
        }

        const result = await callAPI(`forex`, 'POST', { currency: 'EUR', conv_rate_USD: parsedRate })

        if (result.success) {
            setIsEditing(false)
            if (showSystemMessage) {
                showSystemMessage('success', 'Currency rate updated.')
            }
            refetch()
        } else {
            if (showSystemMessage) {
                showSystemMessage('error', result.errors.message)
            }
        }
    }

    const handleCancel = () => {
        setDraftRate(currentRate)
        setIsEditing(false)
    }

    return (
        <div className="card mb-2">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div>FOREX</div>
                <span className="badge badge-light">EUR → USD</span>
            </div>
            <div className="p-3">
                {dataLoaded ? (
                    <>
                        <div className="mb-3">
                            <div className="card border-info">
                                <div className="card-header bg-info text-white py-1">
                                    <small className="font-weight-bold">ECB Rates</small>
                                </div>
                                <div className="card-body p-2">
                                    {ecbRatesLoaded ? (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-2">
                                                    <small className="text-muted d-block">Current Rate</small>
                                                    <div className="h5 mb-0 font-weight-bold">
                                                        {ecbCurrentRate !== null ? (
                                                            `$${ecbCurrentRate.toFixed(3)}`
                                                        ) : (
                                                            <span className="text-muted">--</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div>
                                                    <small className="text-muted d-block">12-m Average</small>
                                                    <div className="h5 mb-0 font-weight-bold">
                                                        {ecbAverageRate !== null ? (
                                                           `$${ecbAverageRate.toFixed(3)}`
                                                        ) : (
                                                            <span className="text-muted">--</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-2">
                                            <small className="text-muted">Loading ECB rates...</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="d-flex align-items-center">
                                <div className="mr-2">MaxBounty EUR to USD:</div>
                                <h3 className="mt-1 font-weight-bold">
                                    {currentRate !== null ? <Dollars value={currentRate} /> : '--'}
                                </h3>
                            </div>
                            {dataLoaded && currentRate === null && (
                                <div className="text-muted text-danger small mt-1">Rate not found.</div>
                            )}
                            {isEditing ? (
                                <div className="mt-3 form-inline">
                                    <label htmlFor="forex-rate" className="mr-2 mb-0">
                                        New rate
                                    </label>
                                    <input
                                        id="forex-rate"
                                        type="number"
                                        min="0.1"
                                        max="10.0"
                                        step="0.01"
                                        className="form-control form-control-sm mr-2"
                                        value={draftRate}
                                        onChange={(e) => setDraftRate(e.target.value)}
                                        placeholder="e.g. 1.25"
                                    />
                                    <button className="btn btn-primary btn-sm mr-2" onClick={handleSave}>
                                        Confirm
                                    </button>
                                    <button className="btn btn-light btn-sm" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button className="btn btn-primary btn-sm mt-3" onClick={() => setIsEditing(true)}>
                                    Edit rate
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <Loading />
                )}
            </div>
        </div>
    )
}
